/**
 * UranIA PA — Admin skill catalog list (US-029 AC#1+AC#2+AC#4+AC#5).
 *
 * Route `/admin/skills` — pagina list-only del catalogo skill.
 *
 * In Fase 1 il catalogo contiene **solo** il placeholder `core_glossario`
 * seeded da Alembic (US-019). Fase 4 portera' a 13 skill operative (1 core
 * + 11 dominio + 1 orchestrator) caricate via `POST /api/v1/skills/{id}/
 * versions` con envelope encryption AES-256-GCM.
 *
 * AC coperti dal componente:
 *   - AC#1 — Lista skill da `GET /api/v1/skills` (TanStack Query
 *     `useQuery(['admin','skills'])`).
 *   - AC#2 — Ogni riga mostra `skill_id`, `name`, `version`, badge
 *     `is_core`, badge `is_active`.
 *   - AC#3 — Click riga -> detail page `/admin/skills/$skillId` (read-only
 *     in Fase 1; v. `$skillId.tsx`).
 *   - AC#4 — Bootstrap Italia 2.x semantico (table caption, scope=col,
 *     role status) -> axe-core serious=0 (verificato da
 *     `spa-admin-skills-axe.spec.ts`).
 *   - AC#5 — Typecheck strict.
 *
 * Boundary:
 *   - RBAC guard UX: solo `platform_admin` (tenant_admin vede solo le
 *     skill del proprio tenant ma il backend non differenzia per UX —
 *     replicato lo stesso pattern di US-028). L'autorizzazione reale e'
 *     server-side (ruolo realm + tenant filter via RLS in skills-registry).
 *   - Niente body decifrato: AC#3 menziona "admin can see body" ma il
 *     contratto skills-registry GET non espone `body_encrypted` /
 *     plaintext; il body decifrato transita SOLO via `get_skill_body()`
 *     server-side (cap. 13.1). La detail page di Fase 1 si limita ai
 *     metadati (read-only) — l'endpoint `:body` con audit dedicato e'
 *     deferred a Fase 4 US-058+.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { listSkills, SkillsApiError } from '../../../lib/admin/skills-api.ts';
import type { ListSkillsDto, SkillSummaryDto } from '../../../lib/admin/skills-api.ts';
import {
  selectIsAuthenticated,
  selectPrincipal,
  useAuthStore,
} from '../../../lib/auth/auth-store.ts';
import { principalIsPlatformAdmin } from '../../../lib/auth/rbac.ts';
import { Route as RootRoute } from '../../__root.tsx';

const ADMIN_SKILLS_QUERY_KEY = ['admin', 'skills'] as const;

function SkillActiveBadge(props: { readonly active: boolean }): JSX.Element {
  const { t } = useTranslation();
  const variant = props.active ? 'bg-success' : 'bg-secondary';
  const label = props.active
    ? t('admin.skills.status.active')
    : t('admin.skills.status.inactive');
  return (
    <span className={`badge ${variant}`} data-testid="admin-skill-active-badge">
      {label}
    </span>
  );
}

function SkillCoreBadge(props: { readonly isCore: boolean }): JSX.Element {
  const { t } = useTranslation();
  if (!props.isCore) {
    return (
      <span className="badge bg-light text-dark" data-testid="admin-skill-core-badge-domain">
        {t('admin.skills.core.domain')}
      </span>
    );
  }
  return (
    <span className="badge bg-primary" data-testid="admin-skill-core-badge-core">
      {t('admin.skills.core.core')}
    </span>
  );
}

function SkillTable(props: { readonly skills: readonly SkillSummaryDto[] }): JSX.Element {
  const { t } = useTranslation();
  if (props.skills.length === 0) {
    return (
      <p
        className="text-muted"
        role="status"
        aria-live="polite"
        data-testid="admin-skills-empty"
      >
        {t('admin.skills.empty')}
      </p>
    );
  }
  return (
    <div className="table-responsive">
      <table className="table table-striped" data-testid="admin-skills-table">
        <caption className="visually-hidden">{t('admin.skills.table_caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('admin.skills.columns.skill_id')}</th>
            <th scope="col">{t('admin.skills.columns.name')}</th>
            <th scope="col">{t('admin.skills.columns.version')}</th>
            <th scope="col">{t('admin.skills.columns.is_core')}</th>
            <th scope="col">{t('admin.skills.columns.is_active')}</th>
            <th scope="col" className="text-end">
              <span className="visually-hidden">{t('admin.skills.columns.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {props.skills.map((skill) => (
            <tr key={skill.skill_row_id} data-testid={`admin-skills-row-${skill.skill_id}`}>
              <th scope="row">
                <code>{skill.skill_id}</code>
              </th>
              <td>{skill.name}</td>
              <td>
                <code>{skill.version}</code>
              </td>
              <td>
                <SkillCoreBadge isCore={skill.is_core} />
              </td>
              <td>
                <SkillActiveBadge active={skill.is_active} />
              </td>
              <td className="text-end">
                <Link
                  to="/admin/skills/$skillId"
                  params={{ skillId: skill.skill_id }}
                  className="btn btn-sm btn-outline-primary"
                  data-testid={`admin-skills-detail-${skill.skill_id}`}
                >
                  {t('admin.skills.actions.open_detail')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AdminSkillListPage(): JSX.Element {
  const { t } = useTranslation();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const principal = useAuthStore(selectPrincipal);
  const isPlatformAdmin = principalIsPlatformAdmin(principal);

  const query = useQuery<ListSkillsDto, SkillsApiError>({
    queryKey: ADMIN_SKILLS_QUERY_KEY,
    queryFn: listSkills,
    enabled: isAuthenticated && isPlatformAdmin,
    staleTime: 30_000,
  });

  if (!isAuthenticated) {
    return (
      <section className="urania-admin-skills" data-testid="admin-skills-unauthenticated">
        <h1 className="display-5 mb-3">{t('admin.skills.title')}</h1>
        <div
          className="alert alert-info"
          role="region"
          aria-label={t('admin.skills.unauthenticated.aria_label')}
        >
          <p className="mb-2">{t('admin.skills.unauthenticated.message')}</p>
          <Link
            to="/login"
            className="btn btn-primary"
            data-testid="admin-skills-go-to-login"
          >
            {t('admin.skills.unauthenticated.go_to_login')}
          </Link>
        </div>
      </section>
    );
  }

  if (!isPlatformAdmin) {
    return (
      <section className="urania-admin-skills" data-testid="admin-skills-forbidden">
        <h1 className="display-5 mb-3">{t('admin.skills.title')}</h1>
        <div
          className="alert alert-warning"
          role="alert"
          data-testid="admin-skills-forbidden-alert"
        >
          <strong>{t('admin.skills.forbidden.title')}</strong>
          <p className="mb-0">{t('admin.skills.forbidden.body')}</p>
        </div>
      </section>
    );
  }

  return (
    <section className="urania-admin-skills" data-testid="admin-skills-page">
      <nav className="mb-3">
        <Link to="/admin" className="small text-decoration-none">
          ← {t('admin.console.title')}
        </Link>
      </nav>
      <div
        className="rounded p-4 mb-4 position-relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #003d82 0%, #0066cc 100%)',
          color: '#fff',
        }}
      >
        <div
          className="d-flex justify-content-between align-items-start gap-3 flex-wrap position-relative"
          style={{ zIndex: 1 }}
        >
          <div style={{ maxWidth: 720 }}>
            <span
              className="badge mb-2"
              style={{
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            >
              ADMIN · CATALOGO SKILL
            </span>
            <h1 className="h3 mb-2">{t('admin.skills.title')}</h1>
            <p className="mb-0 opacity-90 small">{t('admin.skills.subtitle')}</p>
          </div>
          <Link
            to="/admin/skills/new"
            className="btn btn-light"
            data-testid="admin-skills-new"
          >
            + {t('admin.skills.actions.new_skill')}
          </Link>
        </div>
        <svg
          aria-hidden="true"
          className="position-absolute d-none d-lg-block"
          style={{ top: '-30px', right: '-30px', opacity: 0.12 }}
          width="200"
          height="200"
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M5 0a.5.5 0 0 1 .5.5V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2h1V.5a.5.5 0 0 1 1 0V2A2.5 2.5 0 0 1 14 4.5h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14v1h1.5a.5.5 0 0 1 0 1H14a2.5 2.5 0 0 1-2.5 2.5v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14h-1v1.5a.5.5 0 0 1-1 0V14A2.5 2.5 0 0 1 2 11.5H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2v-1H.5a.5.5 0 0 1 0-1H2A2.5 2.5 0 0 1 4.5 2V.5A.5.5 0 0 1 5 0z" />
        </svg>
      </div>

      {query.isLoading && (
        <p role="status" aria-live="polite" data-testid="admin-skills-loading">
          {t('admin.skills.loading')}
        </p>
      )}

      {query.isError && (
        <div className="alert alert-danger" role="alert" data-testid="admin-skills-error">
          <strong>{t('admin.skills.errors.load_failed')}</strong>
          {query.error.detail !== null && <p className="mb-0">{query.error.detail}</p>}
        </div>
      )}

      {query.isSuccess && (
        <SkillsFiltered items={query.data.items} />
      )}
    </section>
  );
}

type CoreFilter = 'all' | 'core' | 'domain';
type ActiveFilter = 'all' | 'active' | 'inactive';

function SkillsFiltered(props: { readonly items: readonly SkillSummaryDto[] }): JSX.Element {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [coreFilter, setCoreFilter] = useState<CoreFilter>('all');
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return props.items.filter((s) => {
      if (coreFilter === 'core' && !s.is_core) return false;
      if (coreFilter === 'domain' && s.is_core) return false;
      if (activeFilter === 'active' && !s.is_active) return false;
      if (activeFilter === 'inactive' && s.is_active) return false;
      if (q.length === 0) return true;
      return (
        s.skill_id.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        (s.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [props.items, search, coreFilter, activeFilter]);

  const resetFilters = (): void => {
    setSearch('');
    setCoreFilter('all');
    setActiveFilter('all');
  };

  const hasFiltersActive = search.length > 0 || coreFilter !== 'all' || activeFilter !== 'all';

  return (
    <>
      <div className="card shadow-sm border-0 mb-3" data-testid="admin-skills-filters">
        <div className="card-body py-2 px-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className="form-label small text-muted mb-1">
                {t('admin.skills.filters.search_label')}
              </label>
              <input
                type="search"
                className="form-control form-control-sm"
                value={search}
                onChange={(e): void => setSearch(e.target.value)}
                placeholder={t('admin.skills.filters.search_placeholder')}
                data-testid="admin-skills-filter-search"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">
                {t('admin.skills.filters.core_label')}
              </label>
              <select
                className="form-select form-select-sm"
                value={coreFilter}
                onChange={(e): void => setCoreFilter(e.target.value as CoreFilter)}
                data-testid="admin-skills-filter-core"
              >
                <option value="all">{t('admin.skills.filters.core_all')}</option>
                <option value="core">{t('admin.skills.core.core')}</option>
                <option value="domain">{t('admin.skills.core.domain')}</option>
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">
                {t('admin.skills.filters.active_label')}
              </label>
              <select
                className="form-select form-select-sm"
                value={activeFilter}
                onChange={(e): void => setActiveFilter(e.target.value as ActiveFilter)}
                data-testid="admin-skills-filter-active"
              >
                <option value="all">{t('admin.skills.filters.active_all')}</option>
                <option value="active">{t('admin.skills.status.active')}</option>
                <option value="inactive">{t('admin.skills.status.inactive')}</option>
              </select>
            </div>
            <div className="col-md-1 d-flex justify-content-end">
              {hasFiltersActive && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={resetFilters}
                  title={t('admin.skills.filters.reset')}
                  data-testid="admin-skills-filter-reset"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {hasFiltersActive && (
            <p className="small text-muted mb-0 mt-2">
              {t('admin.skills.filters.results_count', {
                shown: filtered.length,
                total: props.items.length,
              })}
            </p>
          )}
        </div>
      </div>
      <SkillTable skills={filtered} />
    </>
  );
}

export const Route = createRoute({
  getParentRoute: () => RootRoute,
  path: '/admin/skills',
  component: AdminSkillListPage,
});

export { ADMIN_SKILLS_QUERY_KEY };
